import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiShield, FiSmile, FiStar, FiTarget } = FiIcons;

const About = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-2">
          About Nakamoto Verse
        </h1>
        <h2 className="text-xl font-medium mb-4 kaas-subtitle">
          KAAS – Kindness as a Service
        </h2>
        <p className="text-xl text-text mb-8 max-w-2xl mx-auto">
          A platform dedicated to spreading kindness, supporting mental health, 
          and creating positive connections in our digital world.
        </p>
      </motion.div>

      <div className="space-y-8">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="clay-card bg-white p-8"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className="clay-button p-3">
              <SafeIcon icon={FiTarget} className="text-2xl text-accent-dark" />
            </div>
            <h2 className="text-2xl font-semibold text-heading">Our Mission</h2>
          </div>
          <p className="text-text leading-relaxed">
            Nakamoto Verse exists to create a safe space where people can share and receive genuine kindness 
            without the pressure of social media metrics or personal exposure. We believe that anonymous 
            acts of kindness can have profound impacts on mental health and overall wellbeing. Our platform 
            is designed specifically for teens, mental health organizations, and wellness communities who 
            want to spread positivity in a meaningful way.
          </p>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="clay-card bg-white p-8"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className="clay-button p-3">
              <SafeIcon icon={FiHeart} className="text-2xl heart-logo" />
            </div>
            <h2 className="text-2xl font-semibold text-heading">How It Works</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="clay-button p-2 mt-1">
                <span className="text-sm font-bold text-heading">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-heading mb-1">Get Your Kindness ID</h3>
                <p className="text-text">Receive a unique, anonymous ID like "gentle-panda-4832" - no personal info required.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="clay-button p-2 mt-1">
                <span className="text-sm font-bold text-heading">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-heading mb-1">Write Your Message</h3>
                <p className="text-text">Craft a kind, uplifting message (up to 250 characters) to share with the world.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="clay-button p-2 mt-1">
                <span className="text-sm font-bold text-heading">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-heading mb-1">Share & Spread Joy</h3>
                <p className="text-text">Get a unique link to share your message via social media, messaging, or however you choose.</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="clay-card bg-white p-8"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className="clay-button p-3">
              <SafeIcon icon={FiShield} className="text-2xl text-accent-dark" />
            </div>
            <h2 className="text-2xl font-semibold text-heading">Privacy & Safety</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-heading mb-2">Complete Anonymity</h3>
              <p className="text-text text-sm">
                No email addresses, no passwords, no personal data collection. 
                Your Kindness ID is stored only on your device.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-heading mb-2">Content Moderation</h3>
              <p className="text-text text-sm">
                All messages are filtered for profanity and harmful content. 
                Only positive, uplifting messages are shared.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-heading mb-2">Mental Health Focus</h3>
              <p className="text-text text-sm">
                Designed with mental health professionals to promote genuine 
                emotional support and positive interactions.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-heading mb-2">No Social Pressure</h3>
              <p className="text-text text-sm">
                No likes, comments, or follower counts. Just pure kindness 
                without the stress of social media metrics.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="clay-card bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 p-8"
        >
          <div className="text-center">
            <div className="clay-button p-4 mx-auto mb-4 w-fit">
              <SafeIcon icon={FiStar} className="text-3xl text-accent-dark" />
            </div>
            <h2 className="text-2xl font-semibold text-heading mb-4">Australian Mental Health Resources</h2>
            <p className="text-text mb-6 max-w-2xl mx-auto">
              Remember: you are valued, you matter, and you deserve kindness. If you're struggling, 
              please reach out for professional support.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="clay-card bg-white p-4">
                <h3 className="font-semibold text-heading mb-2">Lifeline Australia</h3>
                <p className="text-text">Call: 13 11 14</p>
              </div>
              <div className="clay-card bg-white p-4">
                <h3 className="font-semibold text-heading mb-2">Poison Information Centre</h3>
                <p className="text-text">Call: 13 11 26</p>
              </div>
              <div className="clay-card bg-white p-4">
                <h3 className="font-semibold text-heading mb-2">Beyond Blue</h3>
                <p className="text-text">Call: 1300 22 4636</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;